import { Button, Loading, Message, Title } from "@components";

import styles from "styles/pages/reports/reports.module.scss";
import ComparasionOfOperations from "./ComparasionOfOperations";
import OperationsInPeriod from "./OperationsInPeriod";
import OperationsRanking from "./OperationsRanking";
import OperationsRecord from "./OperationsRecord";
import ReportFilters from "pages/Reports/ReportFilters";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Filters from "pages/Reports/Filters";
import { ReportsAPI } from "providers/ReportsAPI";
import { Currency } from "shared/utils/Currency";
import { Trend } from "pages/Reports/Reports";
import { Numbers } from "shared/utils/Numbers";
import { If } from "shared/components/control-statements";
import { OlgaEnvironmentsAPI } from "providers/OlgaEnvironmentsAPI";
import dayjs from "dayjs";
import { UserStorage } from "shared/tools/UserStorage";

const numbers = (data = {}, isCashback, hasUnitsReportFilters) => {
  return [
    {
      title: isCashback ? "Cashback concedido" : "Pontos concedidos",
      count: {
        value: data?.received?.count?.value,
        growth: data?.received?.count?.growth,
      },
      total: {
        value: data?.received?.total?.value,
        growth: data?.received?.total?.growth,
      },
    },
    {
      title: isCashback ? "Cashback consumido" : "Pontos consumidos",
      count: {
        value: data?.used?.count?.value,
        growth: data?.used?.count?.growth,
      },
      total: {
        value: data?.used?.total?.value,
        growth: data?.used?.total?.growth,
      },
    },
    {
      alias: "expired",
      title: isCashback ? "Cashback expirado" : "Pontos expirados",
      count: {
        value: hasUnitsReportFilters ? "---" : data?.expired?.count?.value,
        growth: hasUnitsReportFilters ? null : data?.expired?.count?.growth,
      },
      total: {
        value: hasUnitsReportFilters ? "---" : data?.expired?.total?.value,
        growth: hasUnitsReportFilters ? null : data?.expired?.total?.growth,
      },
    },
    {
      alias: "chargeback",
      title: isCashback ? "Cashback estornado" : "Pontos estornados",
      count: {
        value: hasUnitsReportFilters ? "---" : data?.chargeback?.count?.value,
        growth: hasUnitsReportFilters ? null : data?.chargeback?.count?.growth,
      },
      total: {
        value: hasUnitsReportFilters ? "---" : data?.chargeback?.total?.value,
        growth: hasUnitsReportFilters ? null : data?.chargeback?.total?.growth,
      },
    },
  ];
};

const numbers2 = (data = {}, isBeforeNovember) => {
  return [
    {
      alias: "use_ratio",
      title: "Taxa de Uso",
      value: Numbers.formatPercent(data.use_ratio?.value),
      growth: data.use_ratio?.growth || 0,
      tooltip: "Porcentagem do cashback concedido que é utilizado.",
    },
    {
      alias: "expired_ratio",
      title: "Taxa de expiração",
      value: Numbers.formatPercent(data.expired_ratio?.value),
      growth: data.expired_ratio?.growth || 0,
      tooltip: "Porcentagem do cashback concedido que é expirado.",
    },
    {
      alias: "average_use_time",
      title: "Tempo médio para uso",
      value: Numbers.formatInt(data.average_use_time?.value),
      growth: data.average_use_time?.growth || 0,
      tooltip: "Média de tempo entre a concessão e o uso de cashback.",
    },
  ];
};

const numbers3 = (data = {}, isBeforeNovember) => {
  return [
    {
      title: "Faturamento bruto",
      value: isBeforeNovember
        ? "---"
        : Currency.formatCurrency(data.total?.value),
      growth: isBeforeNovember ? null : data.total?.growth || 0,
      tooltip:
        "Soma do valor total bruto de todos os pedidos utilizando cupons.",
    },
    {
      title: "Faturamento líquido",
      value: isBeforeNovember
        ? "---"
        : Currency.formatCurrency(data.total_with_discounts?.value),
      growth: isBeforeNovember ? null : data.total_with_discounts?.growth || 0,
      tooltip:
        "Soma do valor total líquido de todos os pedidos utilizando cupons.",
    },
    {
      title: "Ticket médio bruto",
      value: isBeforeNovember
        ? "---"
        : Currency.formatCurrency(data.average_ticket?.value),
      growth: isBeforeNovember ? null : data.average_ticket?.growth || 0,
      tooltip:
        "Soma do valor total bruto dos pedidos que utilizaram este cupom, divididos pela quantidade de pedidos que utilizaram cupons.",
    },
    {
      title: "Ticket médio líquido",
      value: isBeforeNovember
        ? "---"
        : Currency.formatCurrency(data.average_ticket_with_discounts?.value),
      growth: isBeforeNovember
        ? null
        : data.average_ticket_with_discounts?.growth || 0,
      tooltip:
        "Soma do valor total líquido dos pedidos que utilizaram este cupom, divididos pela quantidade de pedidos que utilizaram cupons.",
    },
  ];
};

const numbers4 = (data = {}, isBeforeNovember) => {
  return [
    {
      alias: "total_cost",
      title: "Custos com disparos",
      value: Currency.formatCurrency(data.send_cost?.value),
      growth: data.send_cost?.growth || 0,
    },
    {
      alias: "discounts_cost",
      title: "Descontos totais",
      value: Currency.formatCurrency(data.discounts?.value),
      growth: data.discounts?.growth || 0,
    },
    {
      alias: "total_cost",
      title: "Custos totais",
      value: Currency.formatCurrency(data.cost?.value),
      growth: data.cost?.growth || 0,
    },
    {
      title: "ROI do cupom",
      value: isBeforeNovember ? "---" : Numbers.formatInt(data.roi?.value || 0),
      growth: isBeforeNovember ? null : data.roi?.growth || 0,
    },
  ];
};

const BigNumber = ({ data, isCashback, hasReportFilters }) => {
  return (
    <div className={styles.bigNumber}>
      <div className="d-flex flex-column w-100">
        <div className={styles.value}>
          <p>
            <If cond={data.total.value !== "---"}>
              {isCashback
                ? Currency.formatCurrency(+data.total.value)
                : Numbers.formatInt(+data.total.value)}
            </If>
            <If cond={data.total.value === "---"}>---</If>
          </p>
          <If cond={!hasReportFilters}>
            <Trend
              growth={+data?.total.growth}
              invertColorSign={[
                "expired_ratio",
                "average_use_time",
                "total_cost",
                "send_cost",
                "expired",
                "chargeback",
                "cost",
                "discounts_cost",
                "total_cost",
                "expired",
                "chargeback",
              ].includes(data.alias)}
              tooltip="Comparado com o mesmo periodo do mês anterior"
            />
          </If>
        </div>
        <div className={styles.value}>
          <p>
            <If cond={data.total.value !== "---"}>
              {Numbers.formatInt(+data.count.value)}
            </If>
            <If cond={data.total.value === "---"}>---</If>

            <span>(OPS)</span>
          </p>
          <If cond={!hasReportFilters}>
            <Trend
              growth={+data?.count.growth}
              invertColorSign={[
                "expired_ratio",
                "average_use_time",
                "total_cost",
                "send_cost",
                "expired",
                "chargeback",
                "cost",
                "discounts_cost",
                "total_cost",
                "expired",
                "chargeback",
              ].includes(data.alias)}
              tooltip="Comparado com o mesmo periodo do mês anterior"
            />
          </If>
        </div>

        <div className={styles.title}>
          <p>{data.title}</p>
        </div>
      </div>
    </div>
  );
};

const BigNumberBalance = ({ data = {}, hasUnitsReportFilters }) => {
  const program = localStorage.getItem("cashbackProgram")
    ? JSON.parse(localStorage.getItem("cashbackProgram"))
    : null;

  const isCashback = program?.type !== "POINT";

  return (
    <div className={styles.bigNumberOutlined}>
      <div className="d-flex flex-column w-100">
        <div className="d-flex align-center justify-between">
          <div className="d-flex align-center gap-10">
            <i
              className="far fa-hand-holding-dollar"
              style={{ color: "#6F767E" }}
            />
            <If cond={!hasUnitsReportFilters}>
              <p className={styles.number}>
                {isCashback
                  ? Currency.formatCurrency(data.total_available?.value)
                  : Numbers.formatInt(data.total_available?.value)}
              </p>
            </If>
            <If cond={hasUnitsReportFilters}>---</If>
          </div>
        </div>
        <div className={styles.title}>
          <p>{isCashback ? "Cashback" : "Pontos"} em saldo</p>
          <span
            className={styles.info}
            title="Total de cashback disponível para uso na carteira de clientes."
          >
            <i className="fal fa-info-circle" />
          </span>
        </div>
      </div>
    </div>
  );
};

const BigNumber2 = ({ data, hasReportFilters, hasUnitsReportFilters }) => {
  return (
    <div className={styles.bigNumber}>
      <div className="d-flex flex-column">
        <div className="d-flex align-center">
          <If cond={!hasUnitsReportFilters}>
            <If cond={data.title === "Tempo médio para uso"}>
              <p className={styles.number}>{data.value} dias</p>
            </If>
            <If cond={data.title !== "Tempo médio para uso"}>
              <p className={styles.number}>{data.value}</p>
            </If>
          </If>
          <If cond={hasUnitsReportFilters}>
            <p className={styles.number}>---</p>
          </If>
          <If cond={!hasReportFilters}>
            <Trend
              growth={data.growth}
              invertColorSign={[
                "expired_ratio",
                "average_use_time",
                "total_cost",
                "send_cost",
                "expired",
                "chargeback",
                "cost",
                "discounts_cost",
              ].includes(data.alias)}
              tooltip="Comparado com o mesmo periodo do mês anterior"
            />
          </If>
        </div>

        <div className={styles.title}>
          <p>{data.title}</p>
          <span className={styles.info} title={data.tooltip}>
            <i className="fal fa-info-circle" />
          </span>
        </div>
      </div>
    </div>
  );
};

export default function Index() {
  const [openFilters, setOpenFilters] = useState(false);
  const [filters, setFilters] = useState({});
  const [dateFilters, setDateFilters] = useState({});
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({});

  const [indicatorsData, setIndicatorsData] = useState({});

  const filtersRef = useRef(null);

  const provider = useMemo(() => new ReportsAPI(), []);

  const program = localStorage.getItem("cashbackProgram")
    ? JSON.parse(localStorage.getItem("cashbackProgram"))
    : null;

  const isCashback = program?.type !== "POINT";

  const fetchData = async () => {
    const response = await provider.loyaltyOperations(null, filters);
    setData(response);
  };

  const fetchIndicators = async () => {
    const response = await provider.loyaltyIndicators(filters);
    setIndicatorsData(response);
  };

  useEffect(() => {
    setLoading(true);

    Promise.all([fetchData(), fetchIndicators()]).finally(() => {
      setLoading(false);
    });
  }, [filters]);

  const [label_dict, setLabelDict] = useState(new Map());

  const fetchUnits = async () => {
    const response_units = await OlgaEnvironmentsAPI.getUnits();
    const response_brands = await OlgaEnvironmentsAPI.getBrands();

    const units = response_units.getData([]);
    const brands = response_brands.getData([]);

    setLabelDict(
      new Map([
        ...units.map((unit) => [`units_${unit.id}`, unit.name]),
        ...brands.map((brand) => [`brands_${brand.id}`, brand.name]),
      ])
    );
  };

  useEffect(() => {
    fetchUnits();
  }, [filters]);

  function deepMergeArraysByKey(obj1, obj2) {
    const result = {};

    for (const key of Object.keys(obj1)) {
      const arr1 = obj1[key] || [];
      const arr2 = obj2[key] || [];

      const idKey = Object.keys(arr1[0] || arr2[0] || {}).find(
        (k) => k.endsWith("_id") || k === "origin"
      );

      result[key] = arr1.map((item1) => {
        const match = arr2.find((item2) => item2[idKey] === item1[idKey]);
        return { ...item1, ...match };
      });
    }

    return result;
  }

  const distributionMerged = deepMergeArraysByKey(
    data?.distribution || {},
    indicatorsData?.details || {}
  );

  const mergedPeriods = data?.period?.map((item1) => {
    const match = indicatorsData?.periods?.find(
      (item2) => item2.date === item1.date
    );
    return { ...item1, ...match };
  });

  const isSameMonth = dayjs.utc(filters?.start_date).isSame(dayjs(), "month");
  const hasReportFilters =
    filters?.period ||
    filters.unit_id?.length > 0 ||
    filters.origin?.length > 0;

  const november2025 = dayjs("2025-11-01T00:00:00.000Z");
  const october2025 = dayjs("2025-10-24T00:00:00.000Z");

  const currentBeforeNovember = dayjs(filters?.start_date || "").isBefore(
    november2025
  );

  const currentBeforeOctober = dayjs(filters?.start_date || "").isBefore(
    october2025
  );

  const isBeforeNovember =
    currentBeforeNovember ||
    filters?.period === "FROM_THE_BEGINNING" ||
    dayjs().isBefore(november2025);

  const isBeforeOctober =
    currentBeforeOctober ||
    filters?.period === "FROM_THE_BEGINNING" ||
    dayjs().isBefore(october2025);

  const hasUnitsReportFilters =
    filters.coupons?.length > 0 ||
    filters.unit_id?.length > 0 ||
    filters.origin?.length > 0;

  return (
    <>
      <Title
        title="Indicadores de cashback"
        backTo={{
          url: "/relatorios/fidelizacao",
          name: "Fidelização",
        }}
      >
        <Filters
          onRemoveFilter={(filter) => {
            filtersRef.current?.removeFilter(filter);
          }}
          filters={filters}
          units={label_dict}
        />
        <Button onClick={() => setOpenFilters(true)}>Filtrar</Button>
      </Title>

      <If cond={loading}>
        <Loading fullContent />
      </If>

      <If cond={isBeforeOctober}>
        <Message type="warning" className="mb-15">
          Alguns dados de origens não Olga anteriores a 24/10 podem não ser exibidos.
        </Message>
      </If>

      <div className={styles.bigNumbersIndicators4}>
        {numbers3(indicatorsData?.statistics, isBeforeNovember).map((item) => (
          <BigNumber2
            key={item.title}
            data={item}
            hasReportFilters={hasReportFilters}
          />
        ))}
      </div>
      <div className={styles.bigNumbersIndicators4}>
        {numbers4(indicatorsData?.statistics, isBeforeNovember).map((item) => (
          <BigNumber2
            key={item.title}
            data={item}
            hasReportFilters={hasReportFilters}
          />
        ))}
      </div>
      <div className={styles.bigNumbersIndicators3}>
        {numbers2(indicatorsData?.statistics, isBeforeNovember).map((item) => (
          <BigNumber2
            key={item.title}
            data={item}
            hasReportFilters={hasReportFilters}
            hasUnitsReportFilters={hasUnitsReportFilters}
          />
        ))}
      </div>

      <div className={styles.bigNumbersOperations}>
        {numbers(data.statistics, isCashback, hasUnitsReportFilters).map(
          (item) => (
            <BigNumber
              key={item.title}
              data={item}
              isCashback={isCashback}
              hasReportFilters={hasReportFilters}
            />
          )
        )}
        <BigNumberBalance
          data={indicatorsData?.statistics}
          hasUnitsReportFilters={hasUnitsReportFilters}
        />
      </div>

      <If cond={filters?.period !== "SPECIFIC_DAY"}>
        <OperationsInPeriod
          data={mergedPeriods || []}
          isCashback={isCashback}
          isBeforeNovember={isBeforeNovember}
          hasUnitsReportFilters={hasUnitsReportFilters}
        />
      </If>
      <ComparasionOfOperations
        data={distributionMerged}
        label_dict={label_dict}
        isCashback={isCashback}
        isBeforeNovember={isBeforeNovember}
        hasUnitsReportFilters={hasUnitsReportFilters}
      />
      <OperationsRecord isCashback={isCashback} filters={filters} />
      <OperationsRanking isCashback={isCashback} filters={filters} />

      <ReportFilters
        ref={filtersRef}
        open={openFilters}
        filters={filters}
        dateFiltersProp={dateFilters}
        ignoreBlockOrigins
        onClose={() => setOpenFilters(false)}
        onUpdateFilters={(filters) => {
          setFilters(filters);
        }}
        hideOrigins={!UserStorage.isGodMode}
        onUpdateDateFilters={(filters) => {
          setDateFilters(filters);
        }}
      />
    </>
  );
}
